import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { ConfirmDialog, ContentHeader, Field, Modal, MoneyInput, QueryStatus, useToast } from "@cx/ui";
import type { FinancialAccountView } from "@cx/contracts";
import { ACCOUNT_KINDS, formatBRL, type AccountKind } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { ACCOUNT_KIND_LABEL } from "./labels";

type Values = {
  name: string;
  kind: AccountKind;
  bankCode: string | null;
  agency: string | null;
  accountNumber: string | null;
  openingBalanceCents: number;
  openingDate: string | null;
  active: boolean;
};
const EMPTY: Values = {
  name: "",
  kind: "bank",
  bankCode: null,
  agency: null,
  accountNumber: null,
  openingBalanceCents: 0,
  openingDate: null,
  active: true,
};
const money = (c: number | null) => (c === null ? "—" : formatBRL(c));

/** Caixas e bancos (RF-FIN-02): contas com saldo atual; o extrato abre na conta. */
export function AccountsPage(): ReactElement {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const key = ["fin-accounts", companyId];
  const list = useQuery({
    queryKey: key,
    queryFn: () => api<{ data: FinancialAccountView[] }>("/financial-accounts"),
  });
  const [editing, setEditing] = useState<{ id?: string; values: Values } | null>(null);
  const [deleting, setDeleting] = useState<FinancialAccountView | null>(null);
  const changed = () => qc.invalidateQueries({ queryKey: key });
  const del = useMutation({
    mutationFn: (id: string) => api(`/financial-accounts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("success", "Conta excluída");
      changed();
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const rows = list.data?.data ?? [];
  const total = rows.every((r) => r.balanceCents !== null)
    ? rows.filter((r) => r.active).reduce((s, r) => s + (r.balanceCents ?? 0), 0)
    : null;

  return (
    <>
      <ContentHeader
        title="Caixas e bancos"
        breadcrumb={["Finanças", "Caixas e bancos"]}
        actions={
          can(perms, "financial_accounts", "create") && (
            <button type="button" className="btn btn-primary" onClick={() => setEditing({ values: EMPTY })}>
              Incluir conta
            </button>
          )
        }
      />
      <div className="box card">
        <QueryStatus loading={list.isLoading} error={list.error} />
        {list.isSuccess && rows.length === 0 && (
          <p className="text-muted p-3 mb-0">
            Nenhuma conta. Cadastre o caixa da loja e as contas bancárias para registrar recebimentos e
            pagamentos.
          </p>
        )}
        {rows.length > 0 && (
          <div className="table-responsive">
            <table className="table align-middle mb-0" aria-label="Caixas e bancos">
              <thead>
                <tr>
                  <th scope="col">Conta</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Banco / agência / conta</th>
                  <th scope="col" className="text-end">
                    Saldo atual
                  </th>
                  <th scope="col">Situação</th>
                  <th scope="col">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Link to="/financas/caixas-e-bancos/$id" params={{ id: a.id }}>
                        {a.name}
                      </Link>
                    </td>
                    <td>{ACCOUNT_KIND_LABEL[a.kind]}</td>
                    <td>{[a.bankCode, a.agency, a.accountNumber].filter(Boolean).join(" / ") || "—"}</td>
                    <td
                      className={`text-end${(a.balanceCents ?? 0) < 0 ? " text-danger" : ""}`}
                      data-testid={`balance-${a.name}`}
                    >
                      {money(a.balanceCents)}
                    </td>
                    <td>{a.active ? "Ativa" : "Inativa"}</td>
                    <td className="text-end text-nowrap">
                      {can(perms, "financial_accounts", "update") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-2"
                          aria-label={`Editar ${a.name}`}
                          onClick={() => setEditing({ id: a.id, values: { ...a } })}
                        >
                          Editar
                        </button>
                      )}
                      {can(perms, "financial_accounts", "delete") && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          aria-label={`Excluir ${a.name}`}
                          disabled={del.isPending}
                          onClick={() => setDeleting(a)}
                        >
                          Excluir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" colSpan={3}>
                    Saldo das contas ativas
                  </th>
                  <td className="text-end fw-semibold">{money(total)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      {editing && (
        <AccountDialog
          id={editing.id}
          initial={editing.values}
          onClose={() => setEditing(null)}
          onSaved={changed}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title="Excluir conta"
        message={`Excluir a conta "${deleting?.name ?? ""}"? Só contas sem lançamentos podem ser excluídas; as demais podem ser inativadas.`}
        confirmLabel="Excluir"
        onConfirm={() => {
          if (deleting) del.mutate(deleting.id);
          setDeleting(null);
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}

function AccountDialog({
  id,
  initial,
  onClose,
  onSaved,
}: {
  id?: string;
  initial: Values;
  onClose(): void;
  onSaved(): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const [v, setV] = useState(initial);
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () =>
      api(id ? `/financial-accounts/${id}` : "/financial-accounts", {
        method: id ? "PUT" : "POST",
        body: JSON.stringify(v),
      }),
    onSuccess: () => {
      toast("success", "Conta salva");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else if (e instanceof ApiFailure && e.code === "duplicate_account") setErrors({ name: e.message });
      else if (e instanceof ApiFailure && e.code === "date_before_opening")
        setErrors({ openingDate: e.message });
      toast("danger", errorMessage(e));
    },
  });
  const text = (k: "bankCode" | "agency" | "accountNumber", max: number) => (
    <input value={v[k] ?? ""} maxLength={max} onChange={(e) => setV({ ...v, [k]: e.target.value || null })} />
  );
  return (
    <Modal
      open={open}
      title={id ? "Editar conta" : "Nova conta"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="Nome" required className="col-md-7" error={errors.name}>
          <input
            value={v.name}
            maxLength={60}
            data-autofocus
            onChange={(e) => setV({ ...v, name: e.target.value })}
          />
        </Field>
        <Field label="Tipo" required className="col-md-5" error={errors.kind}>
          <select
            className="form-select"
            value={v.kind}
            onChange={(e) => setV({ ...v, kind: e.target.value as AccountKind })}
          >
            {ACCOUNT_KINDS.map((k) => (
              <option key={k} value={k}>
                {ACCOUNT_KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </Field>
        {v.kind === "bank" && (
          <>
            <Field label="Banco (código)" className="col-md-4" error={errors.bankCode} hint="Ex.: 001, 341">
              {text("bankCode", 3)}
            </Field>
            <Field label="Agência" className="col-md-4" error={errors.agency}>
              {text("agency", 10)}
            </Field>
            <Field label="Conta" className="col-md-4" error={errors.accountNumber}>
              {text("accountNumber", 20)}
            </Field>
          </>
        )}
        <Field label="Saldo inicial" className="col-md-6" error={errors.openingBalanceCents}>
          <MoneyInput
            value={v.openingBalanceCents}
            onChange={(c) => setV({ ...v, openingBalanceCents: c ?? 0 })}
          />
        </Field>
        <Field
          label="Data de abertura"
          className="col-md-6"
          error={errors.openingDate}
          hint="Lançamentos e baixas só a partir desta data (vazia = data do cadastro)"
        >
          <input
            type="date"
            value={v.openingDate ?? ""}
            onChange={(e) => setV({ ...v, openingDate: e.target.value || null })}
          />
        </Field>
      </div>
      <div className="form-check">
        <input
          id="acc-active"
          type="checkbox"
          className="form-check-input"
          checked={v.active}
          onChange={(e) => setV({ ...v, active: e.target.checked })}
        />
        <label className="form-check-label" htmlFor="acc-active">
          Ativa
        </label>
      </div>
    </Modal>
  );
}
